import React, { createContext, useContext, useState, useEffect } from 'react';
import cmsService from 'src/services/cms-service';
import globalStorageService from 'src/global/services/global-storage-service';
import { Announcement } from 'src/global/components/announcements-modal/AnnouncementsModal';
import { useGetMyPreviousLoggedInTimeQuery } from 'src/global/state/api/globalGraphSlice';
import useAuthService from 'src/shared/hooks/useAuthService';

interface AnnouncementsContextType {
    announcements: Announcement[];
    isModalOpen: boolean;
    isLoading: boolean;
    openModal: () => Promise<void>;
    closeModal: () => void;
    checkForNewAnnouncements: () => Promise<void>;
}

const AnnouncementsContext = createContext<AnnouncementsContextType | undefined>(undefined);

export const useAnnouncementsContext = () => {
    const context = useContext(AnnouncementsContext);
    if (!context) {
        throw new Error('useAnnouncementsContext must be used within an AnnouncementsProvider');
    }
    return context;
};

interface AnnouncementsProviderProps {
    children: React.ReactNode;
}

export const AnnouncementsProvider: React.FC<AnnouncementsProviderProps> = ({ children }) => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasCheckedAnnouncements, setHasCheckedAnnouncements] = useState(false);
    const { isAuthenticated } = useAuthService();
    // Get previous login time from the new endpoint
    // Do not query if the user is not authenticated
    const { data: lastLoginTimestamp, isFetching: isLastLoginTimestampLoading } = useGetMyPreviousLoggedInTimeQuery(undefined, {
        skip: !isAuthenticated,
    });

    // Check if modal has been shown in current session
    const hasModalBeenShownInSession = (): boolean => {
        return globalStorageService.getAnnouncementsModalShown();
    };

    // Mark modal as shown in current session
    const markModalAsShownInSession = (): void => {
        globalStorageService.setAnnouncementsModalShown(true);
    };

    const loadAnnouncements = async () => {
        setIsLoading(true);
        try {
            const allAnnouncements = await cmsService.getAnnouncements();
            setAnnouncements(allAnnouncements);
        } catch (error) {
            console.error('Failed to load announcements:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const checkForNewAnnouncements = async () => {
        if (hasCheckedAnnouncements) {
            return;
        }

        setIsLoading(true);
        try {
            const allAnnouncements = await cmsService.getAnnouncements();

            // Always set all announcements for the menu
            setAnnouncements(allAnnouncements);

            // Don't show modal automatically if it has already been shown in this session
            if (hasModalBeenShownInSession()) {
                setHasCheckedAnnouncements(true);
                setIsLoading(false);
                return;
            }

            if (!lastLoginTimestamp) {
                // First time user, show all announcements
                if (allAnnouncements.length > 0) {
                    setIsModalOpen(true);
                    markModalAsShownInSession();
                }
                setHasCheckedAnnouncements(true);
                return;
            }

            // Filter announcements created after last login for modal display
            const newAnnouncements = allAnnouncements.filter(announcement => {
                const announcementDate = new Date(announcement.publishedAt);
                const lastLoginDate = new Date(lastLoginTimestamp);
                return announcementDate > lastLoginDate;
            });

            if (newAnnouncements.length > 0) {
                setIsModalOpen(true);
                markModalAsShownInSession();
            }
            setHasCheckedAnnouncements(true);
        } catch (error) {
            console.error('Failed to check for announcements:', error);
            setHasCheckedAnnouncements(true);
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = async () => {
        // Fetch all announcements and show them
        setIsLoading(true);
        try {
            const allAnnouncements = await cmsService.getAnnouncements();
            setAnnouncements(allAnnouncements);
            setIsModalOpen(allAnnouncements.length > 0);
            // Mark as shown when manually opened
            if (allAnnouncements.length > 0) {
                markModalAsShownInSession();
            }
        } catch (error) {
            console.error('Failed to fetch announcements:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        if (isAuthenticated && !isLastLoginTimestampLoading) {
            // Always load announcements, regardless of whether we've checked them
            loadAnnouncements();
            // Only check for new announcements if we haven't already
            if (!hasCheckedAnnouncements) {
                checkForNewAnnouncements();
            }
        }
    }, [isAuthenticated, lastLoginTimestamp, isLastLoginTimestampLoading]);

    const value: AnnouncementsContextType = {
        announcements,
        isModalOpen,
        isLoading,
        openModal,
        closeModal,
        checkForNewAnnouncements,
    };

    return <AnnouncementsContext.Provider value={value}>{children}</AnnouncementsContext.Provider>;
};
